/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { LoyaltyStatus } from '@/extensions/nemesis/nemesis.server';

/**
 * Compact loyalty-tier badge sourced from the head-side loyalty vendor integration seam
 * (`@/extensions/nemesis/nemesis.server`) — e.g. "Gold · 1,240 pts". Renders nothing for guests,
 * whose `loyaltyStatus` promise resolves to a neutral status with zero points. Deliberately not
 * localized via `t()` — this is load-test scaffolding, not merchant-facing UI copy (see module doc
 * on `LoyaltyStatus`), so it doesn't warrant new translation keys across every locale. Rendered
 * directly (marker-wrapped, not via a `UITarget`) on both the PDP and cart routes.
 */
export function LoyaltyStatusBadge({ status }: { status: LoyaltyStatus }) {
    if (status.points === 0) {
        return null;
    }
    const tierLabel = status.tier.charAt(0).toUpperCase() + status.tier.slice(1);
    return (
        <p className="mt-2 text-sm text-muted-foreground">
            {tierLabel} · {status.points.toLocaleString()} pts
        </p>
    );
}

/** Skeleton fallback for `LoyaltyStatusBadge` — below the fold, so kept minimal. */
export function LoyaltyStatusBadgeSkeleton() {
    return <div className="mt-2 h-4 w-20 animate-pulse rounded bg-muted" aria-hidden="true" />;
}
